import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Calendar, MapPin, Clock, ExternalLink, ChevronLeft, ChevronRight, Users, Gift, Globe, Footprints, ArrowUpRight, X, SlidersHorizontal } from 'lucide-react';
import PlatformIcon from '@/components/map/PlatformIcon';
import PageMeta from '@/components/PageMeta';
import axios from 'axios';
import regionsData from '@/data/regions.json';
import useInferredCountry from '@/hooks/useInferredCountry';
import COUNTRY_NAMES from '@/data/countryNames';
import { safeHref } from '../utils/url';

const API = '/api';


const REGION_LIST = Object.entries(regionsData).map(([key, val]) => ({
  key,
  name: val.name,
  countries: val.countries,
}));

// Filter chip component matching Directory.js
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
    {label}
    <button onClick={onRemove} className="hover:text-primary/70 transition-colors ml-0.5" aria-label={`Remove ${label} filter`}>
      <X className="w-3 h-3" />
    </button>
  </span>
);

const Events = ({ defaultCountry = '', defaultRegion = '' } = {}) => {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [meetupGroups, setMeetupGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('calendar');

  // Region / Country / Group filters
  // Initialise from route props (EventsCountry / EventsRegion pass these)
  const [selectedRegion, setSelectedRegion] = useState(() => {
    if (defaultRegion) return defaultRegion;
    if (defaultCountry) {
      const entry = REGION_LIST.find(r => r.countries.includes(defaultCountry));
      return entry ? entry.key : '';
    }
    return '';
  });
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry || '');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const groupDropdownRef = useRef(null);
  const countryManuallySet = useRef(Boolean(defaultCountry || defaultRegion));

  // Update document title based on active scope
  useEffect(() => {
    if (defaultCountry) {
      const name = COUNTRY_NAMES[defaultCountry] || defaultCountry;
      document.title = `Upcoming Bitcoin Events in ${name} — OrangeLabel`;
    } else if (defaultRegion) {
      const regionName = regionsData[defaultRegion]?.name || defaultRegion;
      document.title = `${regionName} Bitcoin Events — OrangeLabel`;
    } else {
      document.title = 'Bitcoin Events — OrangeLabel';
    }
    return () => { document.title = 'OrangeLabel'; };
  }, [defaultCountry, defaultRegion]);

  const { iso2: geoIso2, ready: geoReady } = useInferredCountry();

  // Auto-set region (only) from geo-inferred country on /events root.
  // Setting a country too would hide legitimate events from neighbours in the
  // same region — e.g. a user in AU missing NZ meetups on the calendar.
  // Route-scoped pages (/events/:iso2, /events/region/:slug) keep their defaults.
  useEffect(() => {
    // geoIso2 is null when the country can't be inferred (VPN/proxy/unknown IP);
    // `ready` still flips true, so guard both or `.toUpperCase()` throws and
    // blank-screens the whole Events page for that visitor segment.
    if (!geoReady || !geoIso2) return;
    if (countryManuallySet.current) return;
    const upper = geoIso2.toUpperCase();
    const regionEntry = REGION_LIST.find(r => r.countries.includes(upper));
    if (regionEntry) setSelectedRegion(regionEntry.key);
  }, [geoReady, geoIso2]);

  // Close group dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target)) {
        setGroupDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchEvents = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      // Build meetup-groups URL with server-side filtering when route provides a scope
      let groupsUrl = `${API}/events/meetup-groups`;
      if (defaultCountry) {
        groupsUrl += `?country=${defaultCountry}`;
      } else if (defaultRegion) {
        groupsUrl += `?region=${defaultRegion}`;
      }

      const [eventsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(groupsUrl),
      ]);
      setEvents(eventsRes.data || []);
      setMeetupGroups(groupsRes.data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(true);
    }
    setLoading(false);
  }, [defaultCountry, defaultRegion]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Holidays are year-scoped. The calendar can page into other years via
  // navigateMonth, so refetch keyed on the visible year — otherwise only the
  // current year's Bitcoin holidays ever populate.
  const calendarYear = currentMonth.getFullYear();
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/events/holidays?year=${calendarYear}`)
      .then(res => { if (active) setHolidays(res.data || []); })
      .catch(err => { console.error('Error fetching holidays:', err); });
    return () => { active = false; };
  }, [calendarYear]);

  // Cascading countries based on selected region
  const countryOptions = useMemo(() => {
    if (!selectedRegion) {
      const allCodes = Object.values(regionsData).flatMap(r => r.countries);
      return [...new Set(allCodes)]
        .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
        .filter(c => c.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const regionEntry = regionsData[selectedRegion];
    if (!regionEntry) return [];
    return regionEntry.countries
      .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedRegion]);

  const handleRegionChange = (regionKey) => {
    setSelectedRegion(regionKey);
    setSelectedCountry('');
    countryManuallySet.current = true;
  };

  const handleCountryChange = (countryCode) => {
    countryManuallySet.current = true;
    setSelectedCountry(countryCode);
  };

  const toggleGroup = (groupName) => {
    setSelectedGroups(prev =>
      prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]
    );
  };

  // Scope all groups to selectedCountry → selectedRegion → all. Country
  // wins over region (country implies region). Used by both the filter
  // dropdown and the sidebar list so they stay consistent.
  const scopedGroups = useMemo(() => {
    const regionCountries = selectedRegion
      ? new Set(regionsData[selectedRegion]?.countries || [])
      : null;
    return meetupGroups.filter(g => {
      const cc = (g.country_code || '').toUpperCase();
      if (selectedCountry) return cc === selectedCountry;
      if (regionCountries) return regionCountries.has(cc);
      return true;
    });
  }, [meetupGroups, selectedRegion, selectedCountry]);

  // Filter dropdown options layer the search box on top of scopedGroups.
  const filteredGroupOptions = useMemo(() => {
    const q = groupSearch.toLowerCase();
    if (!q) return scopedGroups;
    return scopedGroups.filter(g => (g.name || '').toLowerCase().includes(q));
  }, [scopedGroups, groupSearch]);

  // Sidebar "Bitcoin Groups" card is just the scoped list — no text filter.
  const sidebarGroups = scopedGroups;

  // Client-side filtering of events. allEventsRaw is computed inside the
  // memo so it participates in memoisation — no new array on every render.
  const filteredEvents = useMemo(() => {
    let f = [...events, ...holidays];

    if (selectedRegion) {
      f = f.filter(e => (e.region || '').toLowerCase() === selectedRegion.toLowerCase() || e.is_bitcoin_holiday);
    }
    if (selectedCountry) {
      // No AU fallback — events without a country are only shown on the
      // unscoped /events root (selectedCountry empty).
      f = f.filter(e => (e.country || '').toUpperCase() === selectedCountry || e.is_bitcoin_holiday);
    }
    if (selectedGroups.length > 0) {
      const groupsLower = selectedGroups.map(g => g.toLowerCase());
      f = f.filter(e => e.is_bitcoin_holiday || groupsLower.includes((e.meetup_group_name || '').toLowerCase()));
    }

    return f;
  }, [events, holidays, selectedRegion, selectedCountry, selectedGroups]);

  const hasActiveFilters = selectedRegion || selectedCountry || selectedGroups.length > 0;

  const resetFilters = () => {
    setSelectedRegion('');
    setSelectedCountry('');
    setSelectedGroups([]);
    countryManuallySet.current = false;
  };

  // Active filter chips
  const activeChips = [];
  if (selectedRegion) {
    const regionName = regionsData[selectedRegion]?.name || selectedRegion;
    activeChips.push({ key: 'region', label: regionName, onRemove: () => { setSelectedRegion(''); setSelectedCountry(''); countryManuallySet.current = true; } });
  }
  if (selectedCountry) {
    const countryName = COUNTRY_NAMES[selectedCountry] || selectedCountry;
    activeChips.push({ key: 'country', label: countryName, onRemove: () => { setSelectedCountry(''); countryManuallySet.current = true; } });
  }
  selectedGroups.forEach(g => {
    activeChips.push({ key: `group-${g}`, label: g, onRemove: () => toggleGroup(g) });
  });

  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  }, []);

  const getEventsForDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return filteredEvents.filter(e => e.event_date === dateStr);
  }, [filteredEvents]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  const formatDateMobile = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!endDate || startDate === endDate) return formatDate(startDate);
    return `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
  };

  const isWalkEvent = useCallback((event) => {
    const title = (event.title || '').toLowerCase();
    const groupName = (event.meetup_group_name || '').toLowerCase();
    return title.includes('walk') || groupName.includes('walk');
  }, []);

  const getEventStyle = useCallback((event) => {
    if (event.is_bitcoin_holiday) {
      return {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-700 dark:text-purple-400',
        border: 'border-purple-200 dark:border-purple-800',
        bgFull: 'bg-purple-50 dark:bg-purple-950/30',
        icon: Gift,
        iconColor: 'text-purple-500',
        btnBg: 'bg-purple-500 hover:bg-purple-600'
      };
    }
    if (isWalkEvent(event)) {
      return {
        bg: 'bg-emerald-100 dark:bg-emerald-900/30',
        text: 'text-emerald-700 dark:text-emerald-400',
        border: 'border-emerald-200 dark:border-emerald-800',
        bgFull: 'bg-emerald-50 dark:bg-emerald-950/30',
        icon: Footprints,
        iconColor: 'text-emerald-500',
        btnBg: 'bg-emerald-500 hover:bg-emerald-600'
      };
    }
    return {
      bg: 'bg-neutral-100 dark:bg-neutral-800/50',
      text: 'text-neutral-700 dark:text-neutral-300',
      border: 'border-neutral-200 dark:border-neutral-700',
      bgFull: 'bg-neutral-50 dark:bg-neutral-900/40',
      icon: Users,
      iconColor: 'text-neutral-500',
      btnBg: 'bg-neutral-600 hover:bg-neutral-700'
    };
  }, [isWalkEvent]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const uniqueFilteredEvents = useMemo(() => {
    const seenEvents = new Set();
    return filteredEvents.filter(event => {
      const key = event.id || `${event.event_date}-${event.title}`;
      if (seenEvents.has(key)) return false;
      seenEvents.add(key);
      return true;
    });
  }, [filteredEvents]);

  const groupedEvents = useMemo(() => (
    uniqueFilteredEvents
      .filter(event => event.event_date >= todayStr)
      .reduce((acc, event) => {
        const date = event.event_date;
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
      }, {})
  ), [uniqueFilteredEvents, todayStr]);

  const sortedDates = useMemo(() => Object.keys(groupedEvents).sort(), [groupedEvents]);

  const calendarDays = useMemo(() => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const cells = [];

    for (let i = 0; i < startingDay; i++) {
      cells.push(<div key={`empty-${i}`} className="h-16 sm:h-24 bg-muted/50"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const isToday = new Date().toDateString() === date.toDateString();
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();

      const firstThumb = (dayEvents.find(e => e.local_image_path) || {}).local_image_path || null;
      const singleThumb = firstThumb;
      const dateLabel = date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

      cells.push(
      <div
        key={day}
        onClick={() => setSelectedDate(date)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedDate(date);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={dateLabel}
        data-testid={`calendar-day-${day}`}
        className={`h-16 sm:h-24 border border-border p-1 cursor-pointer transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800/40 focus:outline-none focus:ring-2 focus:ring-primary relative overflow-hidden ${
          isToday ? 'bg-neutral-100 dark:bg-neutral-800/40 border-neutral-300 dark:border-neutral-600' : ''
        } ${isSelected ? 'ring-2 ring-neutral-500' : ''}`}
      >
        {singleThumb && (
          <img
            src={singleThumb}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        {singleThumb && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30 pointer-events-none" />
        )}
        <div className="relative z-10">
          <div className={`text-xs sm:text-sm font-medium ${isToday ? 'text-neutral-700 dark:text-neutral-200' : 'text-foreground'} ${singleThumb ? 'drop-shadow-sm' : ''}`}>
            {day}
          </div>
          <div className="hidden sm:block">
            {dayEvents.slice(0, 2).map((event, idx) => {
              const style = getEventStyle(event);
              return (
                <div
                  key={idx}
                  className={`text-xs rounded px-1 py-0.5 mb-0.5 truncate ${style.bg} ${style.text}`}
                  title={event.title}
                >
                  {event.title}
                </div>
              );
            })}
            {dayEvents.length > 2 && (
              <div className="text-xs text-muted-foreground">+{dayEvents.length - 2}</div>
            )}
          </div>
          <div className="flex gap-0.5 mt-1 sm:hidden flex-wrap">
            {dayEvents.slice(0, 3).map((event, idx) => {
              const style = getEventStyle(event);
              return <div key={idx} className={`w-1.5 h-1.5 rounded-full ${style.btnBg.split(' ')[0]}`} />;
            })}
          </div>
        </div>
      </div>
      );
    }

    return cells;
  }, [currentMonth, selectedDate, getEventsForDate, getEventStyle, getDaysInMonth]);

  // Compute the selected day's events once (used for both the empty check
  // and the rendered list below).
  const selectedDateEvents = useMemo(
    () => (selectedDate ? getEventsForDate(selectedDate) : []),
    [selectedDate, getEventsForDate]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="events-error">
        <div className="max-w-md w-full text-center bg-card border border-border rounded-xl shadow-sm p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">Couldn&apos;t load events</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Something went wrong fetching the events calendar. Please try again.
          </p>
          <button
            onClick={fetchEvents}
            className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const EventCard = ({ event, compact = false }) => {
    const style = getEventStyle(event);
    const IconComponent = style.icon;
    const isMultiDay = event.end_date && event.end_date !== event.event_date;
    const thumbnail = event.local_image_path || null;
    const safeEventUrl = safeHref(event.event_url);

    return (
      <div className={`rounded-lg ${compact ? 'p-3' : 'p-4'} ${style.bgFull} border-l-4 ${style.border}`}>
        {thumbnail && (
          <div className={`${compact ? 'mb-2' : 'mb-3'} rounded-lg overflow-hidden w-full aspect-video`}>
            <img
              src={thumbnail}
              alt={event.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}
        <div className="flex items-start gap-3">
          <div className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0`}>
            <IconComponent className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${style.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className={`font-medium text-foreground ${compact ? 'text-sm' : 'text-base'} line-clamp-2`}>
                  {event.title}
                </h3>
                {event.meetup_group_name && !event.is_bitcoin_holiday && (
                  <p className={`text-xs ${style.text} mt-0.5`}>{event.meetup_group_name}</p>
                )}
              </div>
              {safeEventUrl && (
                <a
                  href={safeEventUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${style.btnBg} text-white ${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg flex items-center justify-center flex-shrink-0 transition-colors`}
                  aria-label={`View details for ${event.title}`}
                  title="View details"
                >
                  <ArrowUpRight className={`${compact ? 'w-4 h-4' : 'w-5 h-5'}`} />
                </a>
              )}
            </div>
            <div className={`flex items-center gap-3 ${compact ? 'mt-1.5' : 'mt-2'} text-xs text-muted-foreground flex-wrap`}>
              {isMultiDay ? (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDateRange(event.event_date, event.end_date)}
                </span>
              ) : event.event_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(event.event_time)}
                </span>
              )}
              {event.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {event.city}{event.state && !compact ? `, ${event.state}` : ''}
                </span>
              )}
            </div>
            {!compact && event.description && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{event.description}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="events-page">
      <PageMeta
        title="Bitcoin events & meetups"
        path="/events"
        description="Upcoming Bitcoin meetups, BitDevs nights, and Bitcoin Walks worldwide. Filter by region or country; machine-readable feed at /api/events.jsonld."
      />
      {/* Page header */}
      <div className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Title + legend */}
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className="text-lg sm:text-xl font-heading font-bold text-foreground">
                {defaultCountry
                  ? `${COUNTRY_NAMES[defaultCountry] || defaultCountry} Bitcoin Events`
                  : defaultRegion
                    ? `${regionsData[defaultRegion]?.name || defaultRegion} Bitcoin Events`
                    : 'Bitcoin Events'}
              </h1>
              <div className="flex gap-3 overflow-x-auto">
                <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span>
                  <span className="text-muted-foreground">Events</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span className="text-muted-foreground">Walks</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs whitespace-nowrap">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                  <span className="text-muted-foreground">Holidays</span>
                </div>
              </div>
            </div>

            {/* View Toggle */}
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
              data-testid="view-toggle-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
              aria-label={`Switch to ${viewMode === 'list' ? 'calendar' : 'list'} view`}
            >
              {viewMode === 'list' ? (
                <>
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Calendar</span>
                </>
              ) : (
                <>
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex flex-wrap gap-2 items-center">
            {/* Region */}
            <select
              value={selectedRegion}
              onChange={(e) => handleRegionChange(e.target.value)}
              className="px-3 py-1.5 border border-border rounded-lg text-xs sm:text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[120px]"
              aria-label="Filter by region"
            >
              <option value="">All Regions</option>
              {REGION_LIST.map(r => (
                <option key={r.key} value={r.key}>{r.name}</option>
              ))}
            </select>

            {/* Country (cascading) */}
            <select
              value={selectedCountry}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="px-3 py-1.5 border border-border rounded-lg text-xs sm:text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[140px]"
              aria-label="Filter by country"
            >
              <option value="">All Countries</option>
              {countryOptions.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>

            {/* Meetup group multi-select */}
            {meetupGroups.length > 0 && (
              <div className="relative" ref={groupDropdownRef}>
                <button
                  onClick={() => setGroupDropdownOpen(prev => !prev)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                    selectedGroups.length > 0
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Groups
                  {selectedGroups.length > 0 && (
                    <span className="ml-0.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 leading-none">
                      {selectedGroups.length}
                    </span>
                  )}
                </button>
                {groupDropdownOpen && (
                  <div className="absolute top-full mt-1 left-0 z-50 w-64 bg-card border border-border rounded-xl shadow-lg p-2 max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      placeholder="Search groups..."
                      value={groupSearch}
                      onChange={(e) => setGroupSearch(e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border border-border rounded-lg mb-2 bg-background focus:outline-none"
                    />
                    {filteredGroupOptions.map(group => {
                      const checked = selectedGroups.includes(group.name);
                      return (
                        <label key={group.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGroup(group.name)}
                            className="accent-primary"
                          />
                          <span className="truncate text-foreground">{group.name}</span>
                        </label>
                      );
                    })}
                    {filteredGroupOptions.length === 0 && (
                      <p className="text-xs text-muted-foreground px-2 py-1">No groups found.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {hasActiveFilters && (
              <button onClick={resetFilters} className="text-xs text-primary hover:underline whitespace-nowrap">
                Clear all
              </button>
            )}
          </div>

          {/* Active filter chips */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {activeChips.map(chip => (
                <FilterChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {viewMode === 'calendar' ? (
              <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
                {/* Calendar Header */}
                <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border">
                  <button
                    onClick={() => navigateMonth(-1)}
                    data-testid="prev-month-btn"
                    className="p-1.5 sm:p-2 hover:bg-muted rounded-lg transition-colors text-foreground"
                  >
                    <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <h2 className="text-sm sm:text-lg font-semibold text-foreground" data-testid="current-month">
                    {currentMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                  </h2>
                  <button
                    onClick={() => navigateMonth(1)}
                    data-testid="next-month-btn"
                    className="p-1.5 sm:p-2 hover:bg-muted rounded-lg transition-colors text-foreground"
                  >
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 bg-muted">
                  {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((key, i) => (
                    <div key={key} className="py-1.5 sm:py-2 text-center text-[10px] sm:text-sm font-medium text-muted-foreground">
                      <span className="sm:hidden">{['S', 'M', 'T', 'W', 'T', 'F', 'S'][i]}</span>
                      <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</span>
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {calendarDays}
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
                <div className="p-3 sm:p-4 border-b border-border">
                  <h2 className="text-base sm:text-lg font-semibold text-foreground">Upcoming Events</h2>
                </div>
                <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
                  {sortedDates.length === 0 ? (
                    <div className="p-6 sm:p-8 text-center text-muted-foreground">
                      No upcoming events for the selected filters.
                    </div>
                  ) : (
                    sortedDates.map(date => (
                      <div key={date} className="p-3 sm:p-4">
                        <div className="text-xs sm:text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2 sm:mb-3">
                          <span className="sm:hidden">{formatDateMobile(date)}</span>
                          <span className="hidden sm:inline">{formatDate(date)}</span>
                        </div>
                        <div className="space-y-2 sm:space-y-3">
                          {groupedEvents[date].map(event => (
                            <div key={event.id || `${event.event_date}-${event.title}`}>
                              <div className="block sm:hidden">
                                <EventCard event={event} compact={true} />
                              </div>
                              <div className="hidden sm:block">
                                <EventCard event={event} compact={false} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Selected Date Events */}
            {viewMode === 'calendar' && selectedDate && (
              <div className="mt-4 sm:mt-6 bg-card rounded-xl border border-border shadow-sm p-3 sm:p-4" data-testid="selected-date-events">
                <h3 className="font-semibold text-foreground mb-2 sm:mb-3 text-sm sm:text-base">
                  {formatDate(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`)}
                </h3>
                {selectedDateEvents.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No events on this day.</p>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {selectedDateEvents.map(event => (
                      <div key={event.id || `${event.event_date}-${event.title}`}>
                        <div className="block sm:hidden">
                          <EventCard event={event} compact={true} />
                        </div>
                        <div className="hidden sm:block">
                          <EventCard event={event} compact={false} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* Submit Your Meetup */}
            <div className="bg-gradient-to-br from-purple-50 to-orange-50 dark:from-purple-950/30 dark:to-orange-950/30 rounded-xl border border-purple-200 dark:border-purple-800 shadow-sm p-4">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                Host a Bitcoin Event?
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                Get your events automatically listed here by submitting your group's event page.
              </p>
              <a
                href="/submit-meetup"
                className="inline-flex items-center justify-center w-full gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
              >
                Submit Your Meetup
                <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                We support Meetup.com, Bitcoin Walk, and more
              </p>
            </div>

            {/* Meetup Groups — scoped to selected region/country so the
                sidebar list matches whatever the visitor has narrowed to */}
            <div className="bg-card rounded-xl border border-border shadow-sm p-4">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                Bitcoin Groups
                {(selectedRegion || selectedCountry) && (
                  <span className="text-xs font-normal text-muted-foreground ml-auto">
                    {sidebarGroups.length}
                  </span>
                )}
              </h3>
              <div className="space-y-2 sm:space-y-3 max-h-[50vh] overflow-y-auto">
                {sidebarGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No groups in this region yet.</p>
                ) : (
                  sidebarGroups.map((group) => {
                    const isWalk = (group.name || '').toLowerCase().includes('walk') || group.platform === 'bitcoin_walk';
                    const safeGroupUrl = safeHref(group.meetup_url || group.url);
                    return (
                      <div key={group.id} className="flex items-center gap-3 p-2 sm:p-3 border border-border rounded-lg hover:bg-muted/30 transition-colors">
                        {group.local_image_path ? (
                          <img
                            src={group.local_image_path}
                            alt={group.name}
                            loading="lazy"
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0 border border-border"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isWalk
                              ? 'bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/50 dark:to-green-900/50'
                              : 'bg-gradient-to-br from-purple-100 to-orange-100 dark:from-purple-900/50 dark:to-orange-900/50'
                          }`}>
                            {isWalk ? (
                              <Footprints className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-xs sm:text-sm text-foreground truncate">{group.name}</p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {group.city}{group.state ? `, ${group.state}` : ''}{group.country && group.country !== 'Australia' ? `, ${group.country}` : ''}
                          </p>
                        </div>
                        {safeGroupUrl && (
                          <a
                            href={safeGroupUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-muted hover:bg-muted/70 transition-colors"
                            aria-label={`View ${group.name}`}
                            title="Open source page"
                          >
                            <PlatformIcon item={group} className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Events;
